import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RefreshCw, Copy } from 'lucide-react';
import { toast } from "@/hooks/use-toast";

interface DojoSetupStepProps {
  dojoName: string;
  setDojoName: (name: string) => void;
  martialArtType: string;
  setMartialArtType: (type: string) => void;
  martialArtStyle: string;
  setMartialArtStyle: (style: string) => void;
  joinCode: string;
  setJoinCode: (code: string) => void;
}

const DojoSetupStep: React.FC<DojoSetupStepProps> = ({
  dojoName,
  setDojoName,
  martialArtType,
  setMartialArtType,
  martialArtStyle,
  setMartialArtStyle,
  joinCode,
  setJoinCode
}) => {
  useUiLanguage();
  const generateJoinCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = 'DOJO-';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setJoinCode(result);
  };

  const copyJoinCode = async () => {
    if (!joinCode) return;

    try {
      await navigator.clipboard.writeText(joinCode);
      toast({
        title: uiText("Copied!"),
        description: uiText("Join code copied to clipboard."),
      });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to copy code to clipboard."),
        variant: "destructive"
      });
    }
  };

  // Generate initial code if none exists
  React.useEffect(() => {
    if (!joinCode) {
      generateJoinCode();
    }
  }, []);

  const martialArtTypes = [
    'Karate',
    'Taekwondo',
    'Judo',
    'Brazilian Jiu-Jitsu',
    'Muay Thai',
    'Boxing',
    'Mixed Martial Arts',
    'Kung Fu',
    'Aikido',
    'Krav Maga',
    'Other'
  ];

  const getStylesForType = (type: string) => {
    switch (type) {
      case 'Karate':
        return [uiText("Kyokushin"), uiText("Shotokan"), uiText("Goju-Ryu"), uiText("Shito-Ryu"), uiText("Wado-Ryu"), uiText("Uechi-Ryu"), uiText("Other")];
      case 'Taekwondo':
        return [uiText("WTF/WT"), 'ITF', 'ATA', uiText("Other")];
      case 'Judo':
        return [uiText("Kodokan"), 'IJF', uiText("Other")];
      case 'Brazilian Jiu-Jitsu':
        return [uiText("Gracie Jiu-Jitsu"), uiText("Sport BJJ"), uiText("Self-Defense BJJ"), uiText("Other")];
      case 'Muay Thai':
        return [uiText("Traditional"), uiText("Modern"), uiText("Other")];
      case 'Boxing':
        return [uiText("Traditional"), uiText("Olympic"), uiText("Other")];
      case 'Mixed Martial Arts':
        return ['MMA', uiText("Other")];
      case 'Kung Fu':
        return [uiText("Wing Chun"), uiText("Shaolin"), uiText("Wudang"), uiText("Other")];
      case 'Aikido':
        return [uiText("Aikikai"), uiText("Yoshinkan"), uiText("Ki Society"), uiText("Other")];
      case 'Krav Maga':
        return [uiText("Israeli"), uiText("Military"), uiText("Civilian"), uiText("Other")];
      default:
        return [uiText("Other")];
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {uiText("Set Up Your Dojo")}</h3>
        <p className="text-sm text-gray-600">
          {uiText("Configure your dojo details and generate a join code for your students.")}</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="dojoName">{uiText("Dojo Name *")}</Label>
          <Input
            id="dojoName"
            type="text"
            placeholder={uiText("Enter your dojo name")}
            value={dojoName}
            onChange={(e) => setDojoName(e.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="martialArtType">{uiText("Martial Art Type *")}</Label>
          <Select value={martialArtType} onValueChange={setMartialArtType}>
            <SelectTrigger>
              <SelectValue placeholder={uiText("Select martial art type")} />
            </SelectTrigger>
            <SelectContent>
              {martialArtTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="martialArtStyle">{uiText("Martial Art Style *")}</Label>
          <Select value={martialArtStyle} onValueChange={setMartialArtStyle}>
            <SelectTrigger>
              <SelectValue placeholder={uiText("Select martial art style")} />
            </SelectTrigger>
            <SelectContent>
              {getStylesForType(martialArtType).map((style) => (
                <SelectItem key={style} value={style}>
                  {style}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="joinCode">{uiText("Student Join Code")}</Label>
          <p className="text-xs text-gray-500 mb-2">
            {uiText("Students will use this code to join your dojo during registration.")}</p>
          <div className="flex gap-2">
            <Input
              id="joinCode"
              type="text"
              value={joinCode}
              readOnly
              className="font-mono"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={copyJoinCode}
              title={uiText("Copy join code")}
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={generateJoinCode}
              title={uiText("Generate new code")}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
          <p className="text-sm text-blue-800">
            <strong>{uiText("Note:")}</strong> {' '}{uiText("Your dojo will start with a 14-day free trial. You can upgrade to a paid plan anytime from your admin dashboard.")}</p>
        </div>
      </div>
    </div>
  );
};

export default DojoSetupStep;